import { memo, useEffect, useMemo } from 'react';
import { FileIcon, FilePlusIcon, AudioLinesIcon, TriangleAlertIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  Message,
  MessageContent,
  MessageResponse,
  MessageToolbar,
} from '@/components/ai-elements/message';
import { ACTIVITY_LABEL_CLASS, Shimmer } from '@/components/ai-elements/shimmer';
import {
  Reasoning,
  ReasoningContent,
  ReasoningTrigger,
} from '@/components/ai-elements/reasoning';
import {
  Tool,
  ToolHeader,
  type ToolPart,
} from '@/components/ai-elements/tool';
import { useChatExperience } from '../chat-experience';
import { useStreamRevealText } from '../hooks/useStreamRevealText';
import { ChatDocumentCard } from './ChatDocumentCard';
import { ChatAskUserTool } from './ChatAskUserTool';
import { ChatDocumentGenerationTool } from './ChatDocumentGenerationTool';
import { ChatAudioTranscriptionBatch } from './ChatAudioTranscriptionBatch';
import { sanitizeAssistantTextDisplay, selectDisplayedAskUserParts } from '../chat-ask-user-flow';
import {
  collectWebCitationSources,
  getWebSourcePresentation,
  type WebSourcePresentation,
} from '../chat-web-citations';
import {
  buildDocumentSourceEntries,
  collectDocumentCitations,
  getDocumentSourceParts,
  getWebUrlSourceParts,
  hasDegradedRetrieval,
  isRedactedSourcePart,
} from '../chat-document-sources';
import { stripCitationMarkers } from '../chat-citation-markers';
import { CitationBadge, CitationSourcesProvider } from './CitationBadge';
import { ChatSourcesDisclosure } from './ChatSourcesDisclosure';
import { remarkCitationBadges, CITATION_BADGE_TAG } from '../remark-citation-badges';
import type { PluggableList } from 'unified';
import { ChatMessageActions } from './ChatMessageActions';
import { ChatWebActivity } from './ChatWebActivity';
import { getChatToolDisplayName } from '../chat-tool-labels';
import type { ChatUIMessage, ChatUIFilePart } from '../types';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { apiBaseUrl } from '@/lib/auth-client';
import { deriveAssistantVisualState } from '../assistant-visual-state';
import {
  getSafeToolStatusMessage,
  resolveChatToolRenderer,
} from '../chat-tool-renderer-registry';
import {
  countChatUxEvent,
  markChatAssistantActivityVisible,
  markChatFirstTextVisible,
} from '../chat-ux-metrics';
import { areMessageBubblePropsEqual } from './message-bubble-memo';
import {
  ASSISTANT_MESSAGE_CONTENT_CLASS,
  ASSISTANT_MESSAGE_ROOT_CLASS,
  USER_MESSAGE_CONTENT_CLASS,
  USER_MESSAGE_ROOT_CLASS,
  USER_MESSAGE_TEXT_CLASS,
} from './message-bubble-styles';
import { isAudioAttachmentMediaType } from '../chat-attachment-selection';
import { formatAudioDuration } from '../audio-transcription-ui';

const citationAllowedTags = { [CITATION_BADGE_TAG]: ['title'] };
const citationMarkdownComponents = { [CITATION_BADGE_TAG]: CitationBadge };

export type MessageBubbleProps = {
  canRetry: boolean;
  isLoading?: boolean;
  message: ChatUIMessage;
  onEditMessage: (message: ChatUIMessage) => void;
  onRetryAssistant: () => void;
  /** Horodatage du début de tour (durée Reasoning stable cross-remount). */
  reasoningStartedAt?: number;
  /** Faux pour les segments intermédiaires d'un tour scindé par une question `ask_user`. */
  showActions?: boolean;
};

type ChatPart = ChatUIMessage['parts'][number];
type TextPart = Extract<ChatPart, { type: 'text' }>;
type ReasoningPart = Extract<ChatPart, { type: 'reasoning' }>;
type DocumentPart = Extract<ChatPart, {
  type: 'data-document-finish' | 'data-document-open' | 'data-document-preview';
}>;
type RagEmptyPart = Extract<ChatPart, { type: 'data-rag-empty' }>;
type WebResearchActivityPart = Extract<ChatPart, { type: 'data-web-research-activity' }>;
type ToolDisplayPart = ChatPart & {
  errorText?: string;
  input?: unknown;
  output?: unknown;
  state: ToolPart['state'];
  title?: string;
  toolName?: string;
};

function getTextParts(message: ChatUIMessage) {
  const textParts = message.parts.filter((part): part is TextPart => part.type === 'text');
  // Une seule voix : la boucle web multi-steps ré-émet du texte à chaque step
  // pendant le stream, mais le backend ne persiste que la dernière part texte
  // (mapUiToolPartsToStoredParts). Le rendu assistant suit la même règle — le
  // texte courant remplace le précédent au lieu de s'y empiler.
  if (message.role === 'assistant' && textParts.length > 1) {
    return [textParts[textParts.length - 1]!];
  }
  return textParts;
}

function getReasoningParts(message: ChatUIMessage) {
  return message.parts.filter((part): part is ReasoningPart => part.type === 'reasoning');
}

function getFileParts(message: ChatUIMessage): ChatUIFilePart[] {
  return message.parts
    .filter((part) => part.type === 'file')
    .map((part) => part as unknown as ChatUIFilePart);
}

function getDocumentParts(message: ChatUIMessage) {
  const partsByDocumentId = new Map<string, DocumentPart>();

  message.parts
    .filter((part): part is DocumentPart => (
    part.type === 'data-document-finish'
    || part.type === 'data-document-open'
    || part.type === 'data-document-preview'
    ))
    .forEach((part) => {
      const current = partsByDocumentId.get(part.data.documentId);

      if (!current || getDocumentPartPriority(part) >= getDocumentPartPriority(current)) {
        partsByDocumentId.set(part.data.documentId, part);
      }
    });

  return Array.from(partsByDocumentId.values());
}

function getRagEmptyParts(message: ChatUIMessage) {
  return message.parts.filter((part): part is RagEmptyPart => part.type === 'data-rag-empty');
}

function getWebResearchActivityParts(message: ChatUIMessage) {
  return message.parts.filter((part): part is WebResearchActivityPart => (
    part.type === 'data-web-research-activity'
  ));
}

type DocgenPendingPart = Extract<ChatPart, { type: 'data-document-generation-pending' }>;
type AudioTranscriptionBatchPart = Extract<ChatPart, { type: 'data-audio-transcription-batch' }>;

function getDocgenPendingParts(message: ChatUIMessage) {
  return message.parts.filter((part): part is DocgenPendingPart => (
    part.type === 'data-document-generation-pending'
  ));
}

function getAudioTranscriptionBatchParts(message: ChatUIMessage) {
  return message.parts.filter((part): part is AudioTranscriptionBatchPart => (
    part.type === 'data-audio-transcription-batch'
  ));
}

function getFilePartDurationMs(part: ChatUIFilePart) {
  return typeof part.durationMs === 'number' ? part.durationMs : null;
}

function getToolParts(message: ChatUIMessage) {
  return message.parts.filter((part): part is ToolDisplayPart => (
    (part.type === 'dynamic-tool' || part.type.startsWith('tool-'))
    && 'state' in part
  ));
}

function getRawToolName(part: ToolDisplayPart) {
  return part.type === 'dynamic-tool'
    ? part.toolName ?? ''
    : part.type.replace(/^tool-/, '');
}

function getVisibleToolParts(message: ChatUIMessage) {
  return getToolParts(message).filter((part) => {
    const rawName = getRawToolName(part);
    const renderer = resolveChatToolRenderer(rawName);
    return renderer !== 'hidden' && renderer !== 'ask-user';
  });
}

function getDocumentPartPriority(part: DocumentPart) {
  if (part.data.status === 'ready' || part.data.status === 'error') {
    return 3;
  }

  if (part.type === 'data-document-finish') {
    return 2;
  }

  return 1;
}

function getToolName(part: ToolDisplayPart) {
  const rawName = part.type === 'dynamic-tool'
    ? part.toolName ?? 'outil'
    : part.type.replace(/^tool-/, '') || 'outil';

  return getChatToolDisplayName(rawName);
}

function MessageBubbleComponent({
  canRetry,
  isLoading,
  message,
  onEditMessage,
  onRetryAssistant,
  reasoningStartedAt,
  showActions = true,
}: MessageBubbleProps) {
  const { openDocument } = useChatExperience();
  const textParts = getTextParts(message);
  const reasoningParts = getReasoningParts(message);
  const fileParts = getFileParts(message);
  const includeStreamedRagSources = !isLoading;
  const sourceParts = useMemo(
    () => getDocumentSourceParts(message, includeStreamedRagSources),
    [message, includeStreamedRagSources],
  );
  const redactedSourceParts = sourceParts.filter(isRedactedSourcePart);
  const isRetrievalDegraded = hasDegradedRetrieval(sourceParts);
  const webSources = getWebUrlSourceParts(message)
    .flatMap((part): WebSourcePresentation[] => {
      const source = getWebSourcePresentation({ ...part });
      return source ? [source] : [];
    });
  const documentParts = getDocumentParts(message);
  const ragEmptyParts = getRagEmptyParts(message);
  const webResearchActivityParts = getWebResearchActivityParts(message);
  const docgenPendingParts = getDocgenPendingParts(message);
  const audioTranscriptionBatchParts = getAudioTranscriptionBatchParts(message);
  const { parts: askUserToolParts, progress: askUserProgress } = selectDisplayedAskUserParts(message);
  const toolParts = getVisibleToolParts(message);
  const webActivityToolParts = toolParts.filter((part) => (
    resolveChatToolRenderer(getRawToolName(part)) === 'web-activity'
  ));
  // La carte de génération n'est rendue qu'une fois le tool call RÉSOLU. Tant que
  // l'input est en cours de streaming, son schéma ne parse pas et le titre retombe
  // sur « Document riche » : l'utilisateur voyait apparaître un artefact générique
  // qui changeait ensuite d'identité. On garde le texte de préparation jusque-là.
  const documentGenerationToolParts = toolParts.filter((part) => (
    resolveChatToolRenderer(getRawToolName(part)) === 'document-generation'
    && part.state !== 'input-streaming'
    && part.state !== 'input-available'
  ));
  // An unknown tool that failed is a call the model invented (« code_interpreter »):
  // it never ran and says nothing useful, so it is not shown as an error card.
  const genericToolParts = toolParts.filter((part) => (
    resolveChatToolRenderer(getRawToolName(part)) === 'safe-fallback'
    && part.state !== 'output-error'
  ));
  const isUser = message.role === 'user';
  const rawTextContent = sanitizeAssistantTextDisplay(
    textParts.map((part) => part.text).join('\n'),
  );
  const webCitationSources = useMemo(() => collectWebCitationSources(
    message.parts as ReadonlyArray<{ type: string } & Record<string, unknown>>,
  ), [message.parts]);
  // Keyed document passages exist only on new answers; history without keys
  // renders no inline badge and keeps its sources list.
  const documentCitations = useMemo(() => collectDocumentCitations(sourceParts), [sourceParts]);
  // Cited pages lead the sources list, in order of first citation.
  const citedWebKeys = useMemo(() => stripCitationMarkers(
    rawTextContent,
    new Set(webCitationSources.map((source) => source.citationKey)),
    new Set(),
  ).webKeys, [rawTextContent, webCitationSources]);
  // Streamdown's root memo ignores remarkPlugins and components: citations known
  // only at the end of the stream would never get their badges. Remount the
  // markdown when the set of citation keys changes (once per answer at most).
  const citationRenderKey = useMemo(() => [
    ...documentCitations.map((citation) => citation.citationKey),
    ...webCitationSources.map((source) => source.citationKey),
  ].join(','), [documentCitations, webCitationSources]);
  const citationRemarkPlugins = useMemo<PluggableList>(
    () => [[remarkCitationBadges, {
      documentKeys: documentCitations.map((citation) => citation.citationKey),
      webSources: webCitationSources,
    }]],
    [documentCitations, webCitationSources],
  );
  const textContent = rawTextContent;
  const reasoningContent = reasoningParts.map((part) => part.text).join('\n\n').trim();
  // Pacer de reveal : le texte assistant entre dans le DOM à cadence bornée et
  // monotone (l'ordre visuel est garanti par construction — le fondu Streamdown
  // reste sans stagger). Inactif sur les bulles user et l'historique.
  const { isRevealing, text: revealedTextContent } = useStreamRevealText(
    isUser ? '' : textContent,
    Boolean(isLoading && !isUser),
  );
  // SOTA AI Elements : le panel reasoning ne reste "streaming" que tant que
  // la dernière part du message est encore du reasoning (fermeture auto dès
  // que le texte / tools démarrent).
  const isReasoningStreaming = Boolean(
    isLoading && message.parts.at(-1)?.type === 'reasoning',
  );
  const visualState = deriveAssistantVisualState({
    message,
    status: isLoading ? 'streaming' : 'ready',
  });
  // Une entrée par document cité, ouverte sur son premier passage.
  const documentSources = buildDocumentSourceEntries(message.id, sourceParts);

  useEffect(() => {
    if (message.role !== 'assistant') {
      return;
    }

    if (visualState === 'activity' || visualState === 'content') {
      markChatAssistantActivityVisible();
    }

    if (textContent.length > 0) {
      markChatFirstTextVisible();
    }

    // Garde anti-régression : un message assistant réglé (hors stream) qui ne
    // rend rien de visible est une perte de contenu silencieuse à mesurer.
    if (visualState === 'empty') {
      countChatUxEvent('chat.ui.blank_assistant_state_count');
    }
  }, [message.role, textContent.length, visualState]);

  if (message.role === 'assistant' && (visualState === 'empty' || visualState === 'thinking')) {
    return null;
  }

  // A verified web answer arrives in one block after the research: sources and
  // message actions wait until some answer text is actually visible.
  const isAwaitingVisibleText = Boolean(isLoading) && !isUser && revealedTextContent.trim().length === 0;
  const shouldRenderActions = isUser || (showActions && textContent.length > 0 && !isAwaitingVisibleText);

  return (
    <Message
      from={message.role}
      className={isUser ? USER_MESSAGE_ROOT_CLASS : ASSISTANT_MESSAGE_ROOT_CLASS}
    >
      <MessageContent
        className={isUser ? USER_MESSAGE_CONTENT_CLASS : ASSISTANT_MESSAGE_CONTENT_CLASS}
      >
        <div className="flex flex-col gap-3.5">
            {fileParts.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {fileParts.map((part) => {
                  const isAudio = isAudioAttachmentMediaType(part.mediaType) || part.kind === 'audio';
                  const durationLabel = formatAudioDuration(getFilePartDurationMs(part));

                  if (isAudio) {
                    return (
                      <span
                        key={`${message.id}-${part.attachmentId ?? part.url ?? part.filename ?? 'audio'}`}
                        className={[
                          'inline-flex items-center gap-2 rounded-full px-3 py-2 text-xs font-medium',
                          isUser
                            ? 'border border-border/60 bg-background text-current'
                            : 'border border-border/70 bg-muted/35 text-foreground',
                        ].join(' ')}
                      >
                        <AudioLinesIcon className="size-3.5" />
                        <span className="max-w-44 truncate">{part.filename ?? 'Fichier audio'}</span>
                        {durationLabel ? (
                          <span className="text-muted-foreground">{durationLabel}</span>
                        ) : null}
                      </span>
                    );
                  }

                  return (
                  <button
                    type="button"
                    key={`${message.id}-${part.attachmentId ?? part.url ?? part.filename ?? 'file'}`}
                    onClick={() => openDocument({
                      content: '',
                      documentId: part.attachmentId ?? part.url ?? `${message.id}-file`,
                      filename: part.filename ?? null,
                      kind: 'attachment',
                      mediaType: part.mediaType,
                      sourceUrl: part.url ?? (part.attachmentId
                        ? new URL(`/api/chat/attachments/${part.attachmentId}`, apiBaseUrl).toString()
                        : null),
                      status: 'ready',
                      summary: part.filename ?? part.mediaType,
                      title: part.filename ?? 'Fichier joint',
                    })}
                    className={[
                      'inline-flex items-center gap-2 rounded-full px-3 py-2 text-xs font-medium transition',
                      isUser
                        ? 'border border-border/60 bg-background text-current hover:bg-background/80'
                        : 'border border-border/70 bg-muted/35 text-foreground hover:bg-muted/55',
                    ].join(' ')}
                  >
                    <FileIcon className="size-3.5" />
                    <span className="max-w-44 truncate">{part.filename ?? 'Fichier joint'}</span>
                  </button>
                  );
                })}
              </div>
            ) : null}

            {reasoningContent ? (
              <Reasoning
                className="mb-0 w-full"
                defaultOpen={false}
                isStreaming={isReasoningStreaming}
                startedAt={reasoningStartedAt}
              >
                <ReasoningTrigger />
                <ReasoningContent>{reasoningContent}</ReasoningContent>
              </Reasoning>
            ) : null}

            {webResearchActivityParts.length > 0 || webActivityToolParts.length > 0 ? (
              <ChatWebActivity
                activities={webResearchActivityParts.map((part) => part.data)}
                hasAnswerText={textContent.length > 0}
                isStreaming={isLoading}
                toolParts={webActivityToolParts}
              />
            ) : null}

            {textContent ? (
              isUser ? (
                <p className={USER_MESSAGE_TEXT_CLASS}>{textContent}</p>
              ) : (
                <div className="chat-markdown max-w-none text-base leading-7 text-foreground">
                  <CitationSourcesProvider documentCitations={documentCitations} webSources={webCitationSources}>
                    <MessageResponse
                      key={citationRenderKey}
                      allowedTags={citationAllowedTags}
                      additionalRemarkPlugins={citationRemarkPlugins}
                      components={citationMarkdownComponents}
                      isAnimating={Boolean(isLoading) || isRevealing}
                    >
                      {revealedTextContent}
                    </MessageResponse>
                  </CitationSourcesProvider>
                </div>
              )
            ) : null}

            {audioTranscriptionBatchParts.length > 0 ? (
              <div className="flex flex-col gap-3">
                {audioTranscriptionBatchParts.map((part) => (
                  <ChatAudioTranscriptionBatch
                    data={part.data}
                    key={`${message.id}-${part.data.batchId}`}
                  />
                ))}
              </div>
            ) : null}

            {askUserToolParts.length > 0 ? (
              <div className="flex flex-col gap-3 overflow-visible">
                {askUserToolParts.map((toolPart, toolIndex) => (
                  <ChatAskUserTool
                    key={`${message.id}-ask-user-${toolPart.toolCallId}-${toolIndex}`}
                    progress={askUserProgress}
                    toolPart={toolPart}
                  />
                ))}
              </div>
            ) : null}

            {/* Signal de flux seulement : hors du message en cours, il n'annonce plus rien. */}
            {isLoading && docgenPendingParts.length > 0 && documentGenerationToolParts.length === 0 ? (
              <div className={cn('flex items-center', ACTIVITY_LABEL_CLASS)} data-testid="docgen-preparing">
                <Shimmer>
                  {docgenPendingParts[0]?.data.title
                    ? `Préparation du document « ${docgenPendingParts[0].data.title} »…`
                    : 'Préparation du document…'}
                </Shimmer>
              </div>
            ) : null}

            {documentGenerationToolParts.length > 0 ? (
              <div className="flex flex-col gap-3">
                {documentGenerationToolParts.map((toolPart, toolIndex) => (
                  <ChatDocumentGenerationTool
                    errorText={toolPart.errorText}
                    input={toolPart.input}
                    key={`${message.id}-${toolPart.type}-${toolIndex}`}
                    output={toolPart.output}
                    state={toolPart.state}
                  />
                ))}
              </div>
            ) : null}

            {genericToolParts.length > 0 ? (
              <div className="flex flex-col gap-3">
                {genericToolParts.map((toolPart, toolIndex) => (
                  <Tool key={`${message.id}-${toolPart.type}-${toolIndex}`}>
                    <ToolHeader
                      state={toolPart.state}
                      title={toolPart.title}
                      toolName={getToolName(toolPart)}
                    />
                    <p className="border-t border-border/50 px-3 py-3 text-xs text-muted-foreground">
                      {getSafeToolStatusMessage(toolPart.state)}
                    </p>
                  </Tool>
                ))}
              </div>
            ) : null}

            {documentParts.length > 0 ? (
              <div className="flex flex-col gap-3">
                {documentParts.map((documentPart) => (
                  <ChatDocumentCard
                    key={`${message.id}-${documentPart.data.documentId}`}
                    document={documentPart.data}
                    onOpen={openDocument}
                  />
                ))}
              </div>
            ) : null}

            {ragEmptyParts.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {ragEmptyParts.map((part) => (
                  <Button
                    asChild
                    variant="outline"
                    key={`${message.id}-${part.id ?? part.data.actionHref}`}
                  >
                    <Link to={part.data.actionHref}>
                      <FilePlusIcon className="size-4" />
                      <span>{part.data.actionLabel}</span>
                    </Link>
                  </Button>
                ))}
              </div>
            ) : null}

            {isAwaitingVisibleText ? null : (
              <ChatSourcesDisclosure
                citedWebKeys={citedWebKeys}
                documents={documentSources}
                onOpenDocument={openDocument}
                webSources={webSources}
              />
            )}

            {isRetrievalDegraded && !isAwaitingVisibleText ? (
              <p
                className="flex items-start gap-1.5 text-xs text-muted-foreground"
                data-testid="rag-degraded-notice"
                role="status"
              >
                <TriangleAlertIcon aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
                Recherche documentaire partielle : un service de recherche était momentanément indisponible,
                certains passages peuvent être moins pertinents.
              </p>
            ) : null}

            {redactedSourceParts.length > 0 && !isAwaitingVisibleText ? (
              <div className="text-xs text-muted-foreground" role="status">
                {redactedSourceParts.map((part) => (
                  <p key={`${message.id}-${part.sourceId}-redacted`}>
                    {(part as Record<string, unknown>).redactedLabel as string || 'Source non accessible'}
                  </p>
                ))}
              </div>
            ) : null}
        </div>
      </MessageContent>

      {shouldRenderActions ? (
        <MessageToolbar className={isUser ? 'justify-end px-0.5 pt-1' : 'justify-start px-0.5 pt-1'}>
          <ChatMessageActions
            canGiveFeedback={!isLoading}
            canRetry={canRetry}
            message={message}
            onEdit={onEditMessage}
            onRetry={onRetryAssistant}
          />
        </MessageToolbar>
      ) : null}
    </Message>
  );
}

export const MessageBubble = memo(MessageBubbleComponent, areMessageBubblePropsEqual);
